import global from "@/global";

import type {
  ExternalRegisterAdminService,
  ExternalRegisterDeleteNeverLinkedResponse,
  ExternalRegisterDevPurgeRequest,
  ExternalRegisterDevPurgeResponse,
  ExternalRegisterEntriesResponse,
  ExternalRegisterEntryRecord,
  ExternalRegisterMaintenanceEntry,
  ExternalRegisterMaintenanceFilters,
  ExternalRegisterMaintenancePagination,
  ExternalRegisterRetentionEvaluation,
  ExternalRegisterSettings,
  ExternalRegisterSettingsPatch,
} from "./externalRegisterAdminTypes";

type ClientResponse<T> = {
  data?: T;
};

type ExternalRegisterAdminClient = {
  get: (url: string, options?: unknown) => Promise<unknown>;
  post: (url: string, payload?: unknown, options?: unknown) => Promise<unknown>;
  patch: (url: string, payload?: unknown, options?: unknown) => Promise<unknown>;
  delete: (url: string, options?: unknown) => Promise<unknown>;
};

const unwrapResponse = <T>(response: unknown, fallback: T): T => {
  return ((response as ClientResponse<T>)?.data ?? fallback) as T;
};

const compactFilters = (
  filters: ExternalRegisterMaintenanceFilters,
  pagination?: ExternalRegisterMaintenancePagination,
) => ({
  status: filters.status,
  linkStatus: filters.linkStatus,
  ...(filters.type !== "all" ? { type: filters.type } : {}),
  ...(filters.patientId.trim() ? { patientId: filters.patientId.trim() } : {}),
  ...(pagination ? { $limit: pagination.limit, $skip: pagination.skip } : {}),
  includeDetails: true,
});

export const createExternalRegisterAdminService = (
  client: ExternalRegisterAdminClient = global.client as ExternalRegisterAdminClient,
): ExternalRegisterAdminService => ({
  async listEntries(filters, pagination) {
    const response = await client.post(
      "patients/custom/external-register/entries/list",
      compactFilters(filters, pagination),
    );
    return unwrapResponse<ExternalRegisterEntriesResponse>(response, { total: 0, data: [] });
  },

  async createEntry(payload) {
    const response = await client.post(
      "patients/custom/external-register/entries",
      payload,
    );
    return unwrapResponse<ExternalRegisterMaintenanceEntry>(response, {} as ExternalRegisterMaintenanceEntry);
  },

  async updateEntry(entryId: string, payload: Partial<ExternalRegisterEntryRecord>) {
    const response = await client.patch(
      `patients/custom/external-register/entries/${entryId}`,
      payload,
    );
    return unwrapResponse<ExternalRegisterMaintenanceEntry>(response, {} as ExternalRegisterMaintenanceEntry);
  },

  async inactivateEntry(entryId: string, payload: { reason?: string }) {
    const response = await client.post(
      `patients/custom/external-register/entries/${entryId}/inactivate`,
      payload,
    );
    return unwrapResponse<ExternalRegisterMaintenanceEntry>(response, {} as ExternalRegisterMaintenanceEntry);
  },

  async reactivateEntry(entryId: string, payload: { reason?: string }) {
    const response = await client.post(
      `patients/custom/external-register/entries/${entryId}/reactivate`,
      payload,
    );
    return unwrapResponse<ExternalRegisterMaintenanceEntry>(response, {} as ExternalRegisterMaintenanceEntry);
  },

  async unlinkEntry(entryId: string, payload: { reason: string }) {
    const response = await client.post(
      `patients/custom/external-register/entries/${entryId}/unlink`,
      payload,
    );
    return unwrapResponse<ExternalRegisterMaintenanceEntry>(response, {} as ExternalRegisterMaintenanceEntry);
  },

  async relinkEntry(entryId: string, payload: { patientId: string; reason: string }) {
    const response = await client.post(
      `patients/custom/external-register/entries/${entryId}/relink`,
      payload,
    );
    return unwrapResponse<ExternalRegisterMaintenanceEntry>(response, {} as ExternalRegisterMaintenanceEntry);
  },

  async deleteNeverLinkedEntry(entryId: string) {
    const response = await client.delete(
      `patients/custom/external-register/entries/${entryId}`,
    );
    return unwrapResponse<ExternalRegisterDeleteNeverLinkedResponse>(response, { id: entryId, deleted: true });
  },

  async purgeExternalRegister(payload: ExternalRegisterDevPurgeRequest) {
    const response = await client.post(
      "patients/custom/external-register/purge",
      payload,
    );
    return unwrapResponse<ExternalRegisterDevPurgeResponse>(response, {
      enabled: true,
      dryRun: payload.dryRun === true,
      purgedEntries: 0,
      cleanedPatients: 0,
      scannedPatients: 0,
    });
  },

  async getSettings() {
    const response = await client.get("patients/custom/external-register/settings");
    return unwrapResponse<ExternalRegisterSettings>(response, {} as ExternalRegisterSettings);
  },

  async updateSettings(patch: ExternalRegisterSettingsPatch) {
    const response = await client.patch("patients/custom/external-register/settings", patch);
    return unwrapResponse<ExternalRegisterSettings>(response, {} as ExternalRegisterSettings);
  },

  async evaluateRetention() {
    const response = await client.post("patients/custom/external-register/retention/evaluate", {});
    return unwrapResponse<ExternalRegisterRetentionEvaluation>(response, {
      enabled: false,
      evaluatedAt: "",
      wouldEvaluateCount: 0,
      actionedCount: 0,
    });
  },
});

export const externalRegisterAdminService = createExternalRegisterAdminService();
